import { EmptyState } from '@dixa/lib-shared-habla';
import useArticleEditorBackLink from 'articleEditor/hooks/useArticleEditorBackLink';
import useArticleEditorFetch from 'articleEditor/hooks/useArticleEditorFetch';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import s from './provider.module.scss';

export type ArticleData = NonNullable<
  NonNullable<ReturnType<typeof useArticleEditorFetch>['data']>['article']
>;

export const ArticleDataContext = createContext<ArticleData>({} as ArticleData);

type ArticleDataProviderProps = {
  articleId: string;
  children: ReactNode;
};

export const ArticleDataProvider = ({
  articleId,
  children,
}: ArticleDataProviderProps): JSX.Element => {
  const { data, error, loading, refetch } = useArticleEditorFetch({
    articleId,
  });
  const { currentLanguage, setCurrentLanguage } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { to } = useArticleEditorBackLink();

  // Set the initial language loaded
  useEffect(() => {
    if (!data) return;
    if (currentLanguage) return;

    const initialTranslation = data.article?.translations.draft[0].languageId;
    if (!initialTranslation) throw new Error('No initial translation found');
    setTimeout(() => setCurrentLanguage(initialTranslation, 'replaceIn'), 0);
  }, [currentLanguage, data, setCurrentLanguage]);

  // Make sure the initial locale has been set.
  if (!data && loading) {
    return <KnowledgeLoading />;
  }

  if (error || !data) {
    return (
      <EmptyState
        title="Something went wrong"
        description="There was an error fetching the article"
        className={s.errorMessage}
        buttonProps={{
          onClick: () => refetch(),
        }}
      />
    );
  }

  if (!data.article) {
    return (
      <EmptyState
        title="Article not found"
        description="There was an error finding the article, click to go back."
        className={s.errorMessage}
        buttonProps={{
          onClick: () => navigate(to),
        }}
      />
    );
  }

  return (
    <ArticleDataContext.Provider value={data.article}>
      {children}
    </ArticleDataContext.Provider>
  );
};
